import { useTranslation } from 'react-i18next';
import { Pencil, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Badge } from '@ooh/ui/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import type { UserAdminListItem } from '@ooh/contracts';
import { AdminStatusBadge } from '@ooh/ui/components/admin-status-badge';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';

const roleBadgeVariant: Record<string, 'default' | 'secondary' | 'outline'> = {
  admin: 'default',
  sales: 'secondary',
  planner: 'secondary',
  finance: 'outline',
  new_products: 'outline',
};

interface UsersTableProps {
  items: UserAdminListItem[];
  total: number;
  page: number;
  pageSize: number;
  onEdit: (user: UserAdminListItem) => void;
  onPageChange: (page: number) => void;
}

export function UsersTable({
  items,
  total,
  page,
  pageSize,
  onEdit,
  onPageChange,
}: UsersTableProps) {
  const { t } = useTranslation('admin');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Users className="w-10 h-10 text-muted-foreground/40 mb-3" />
        <p className="text-[15px] font-medium text-muted-foreground">{t('users.empty.title')}</p>
        <p className="text-[13px] text-muted-foreground/60 mt-1">{t('users.empty.description')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border/50">
              <TableHead>{t('users.table.email')}</TableHead>
              <TableHead>{t('users.table.name')}</TableHead>
              <TableHead>{t('users.table.role')}</TableHead>
              <TableHead>{t('users.table.status')}</TableHead>
              <TableHead>{t('users.table.createdAt')}</TableHead>
              <TableHead className="px-4 py-3" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((user) => (
              <TableRow key={user.id} listRow={true}>
                <TableCell className="px-4 py-3 text-[13px] text-foreground font-mono">
                  {user.email}
                </TableCell>
                <TableCell className="px-4 py-3 text-[13px] font-medium text-foreground">
                  {user.name}
                </TableCell>
                <TableCell className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((role) => (
                      <Badge
                        key={role.id}
                        variant={roleBadgeVariant[role.code] ?? 'outline'}
                        className="text-xs"
                      >
                        {role.name}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3">
                  <AdminStatusBadge
                    active={user.isActive}
                    activeLabel={t('users.status.active')}
                    inactiveLabel={t('users.status.inactive')}
                    inactiveTone="danger"
                  />
                </TableCell>
                <TableCell className="px-4 py-3 text-[13px] text-muted-foreground">
                  {formatDisplayDate(user.createdAt)}
                </TableCell>
                <TableCell className="px-4 py-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(user)}
                    aria-label={t('users.actions.edit')}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-[13px] text-muted-foreground">
            {t('users.pagination.page', { page, total: totalPages, count: total })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              aria-label={t('users.pagination.prev')}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              aria-label={t('users.pagination.next')}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
